import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useMutation } from "@tanstack/react-query";
import {
  CheckCircle2,
  Loader2,
  MailCheck,
  RotateCcw,
  ShieldCheck,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ParentAuthLayout from "./ParentAuthLayout";
import {
  persistParentPostAuthRedirect,
  readParentPostAuthRedirect,
  PARENT_BOOKING_HUB_PATH,
} from "../../features/parentFlow/parentFlow";
import {
  extractStudentEmail,
  extractStudentJwtToken,
  extractStudentPreAuthToken,
  extractStudentUserRecord,
  isAlreadyVerifiedResponse,
  isStudentVerified,
  studentAuthApi,
} from "../../features/auth/authApi";
import {
  STUDENT_OTP_ATTEMPTS,
  clearStudentVerificationFlow,
  createStudentVerificationFlow,
  decrementStudentOtpAttempts,
  formatStudentOtpTimer,
  getStoredStudentVerificationFlow,
  getStudentOtpRemainingMs,
  hasStudentOtpExpired,
  markStudentOtpExpired,
  persistStudentVerificationFlow,
} from "../../features/auth/studentOtpFlow";
import { maskStudentEmail } from "../../features/auth/studentSession";
import { finalizeParentLogin } from "../../features/parentFlow/parentAuth";

const GENERIC_RESEND_SUCCESS = "A new verification code has been sent.";
const GENERIC_VERIFY_SUCCESS = "Account verified successfully.";
const GENERIC_INVALID_OTP = "The verification code is invalid.";
const GENERIC_EXPIRED_OTP = "This verification code has expired.";
const GENERIC_ATTEMPTS_EXHAUSTED =
  "Too many failed attempts. Please request a new code.";
const GENERIC_PENDING_VERIFICATION = "Please verify your email to continue.";
const GENERIC_ALREADY_VERIFIED = "This account is already verified.";
const GENERIC_FAILURE = "Something went wrong. Please try again.";

const isExpiredOtpResponse = (payload) =>
  String(payload?.message || "").toLowerCase().includes("expire");

const isInvalidOtpResponse = (payload) => {
  const message = String(payload?.message || "").toLowerCase();
  return message.includes("invalid") || message.includes("incorrect");
};

const ParentVerificationPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const redirectPath =
    readParentPostAuthRedirect() || PARENT_BOOKING_HUB_PATH;
  const storedFlow = useMemo(() => getStoredStudentVerificationFlow(), []);
  const [otp, setOtp] = useState("");
  const [flowState, setFlowState] = useState(() =>
    createStudentVerificationFlow(
      {
        ...storedFlow,
        email: String(location.state?.email || storedFlow.email || "")
          .trim()
          .toLowerCase(),
        source: String(
          location.state?.source || storedFlow.source || "parent_signup"
        ).trim(),
      },
      { resetCycle: false }
    )
  );
  const [generalMessage, setGeneralMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState(
    String(location.state?.successMessage || "")
  );
  const [remainingMs, setRemainingMs] = useState(() =>
    getStudentOtpRemainingMs(flowState)
  );
  const lastStatusCheckEmailRef = useRef("");

  useEffect(() => {
    persistParentPostAuthRedirect(readParentPostAuthRedirect());
  }, []);

  useEffect(() => {
    const currentFlow = getStoredStudentVerificationFlow();
    const nextFlow = persistStudentVerificationFlow({
      ...currentFlow,
      email: String(location.state?.email || currentFlow.email || "")
        .trim()
        .toLowerCase(),
      source: String(
        location.state?.source || currentFlow.source || "parent_signup"
      ).trim(),
    });

    setFlowState(nextFlow);
  }, [location.state]);

  useEffect(() => {
    const timerId = window.setInterval(() => {
      const latestFlow = getStoredStudentVerificationFlow();
      const nextRemainingMs = getStudentOtpRemainingMs(latestFlow);

      if (nextRemainingMs === 0 && !hasStudentOtpExpired(latestFlow)) {
        markStudentOtpExpired();
      }

      setFlowState(latestFlow);
      setRemainingMs(nextRemainingMs);
    }, 1000);

    return () => window.clearInterval(timerId);
  }, []);

  const statusCheckMutation = useMutation({
    mutationFn: studentAuthApi.checkVerificationStatus,
    onSuccess: (response) => {
      const payload = response?.data || {};
      const responseData = payload?.data || {};

      if (isStudentVerified(responseData?.isVerified)) {
        clearStudentVerificationFlow();
        setGeneralMessage(GENERIC_ALREADY_VERIFIED);
        navigate("/parent/login", { replace: true });
      } else {
        setGeneralMessage(GENERIC_PENDING_VERIFICATION);
      }
    },
    onError: (error) => {
      const responseData = error?.response?.data || {};
      const message = String(responseData?.message || "").toLowerCase();
      const statusCode = String(responseData?.status_code || "").trim();

      if (statusCode === "201" || message.includes("not verified")) {
        setGeneralMessage(GENERIC_PENDING_VERIFICATION);
        return;
      }

      setGeneralMessage("");
    },
  });

  useEffect(() => {
    const normalizedEmail = String(flowState.email || "").trim().toLowerCase();
    const normalizedSource = String(flowState.source || "").trim().toLowerCase();

    if (!normalizedEmail) {
      lastStatusCheckEmailRef.current = "";
      return;
    }

    if (normalizedSource && normalizedSource !== "parent_signup") {
      lastStatusCheckEmailRef.current = "";
      return;
    }

    if (flowState.preAuthToken) {
      lastStatusCheckEmailRef.current = "";
      return;
    }

    if (lastStatusCheckEmailRef.current === normalizedEmail) return;

    lastStatusCheckEmailRef.current = normalizedEmail;
    statusCheckMutation.mutate({
      email: normalizedEmail,
    });
  }, [flowState.email, flowState.preAuthToken, flowState.source, statusCheckMutation]);

  const verifyOtpMutation = useMutation({
    mutationFn: studentAuthApi.verifySignupOtp,
    onSuccess: (response) => {
      const studentJwt = extractStudentJwtToken(response);
      const profileData = extractStudentUserRecord(response, {
        email: flowState.email,
        isVerified: "1",
        jwtoken: studentJwt,
      });

      if (!studentJwt) {
        setGeneralMessage(GENERIC_FAILURE);
        return;
      }

      clearStudentVerificationFlow();
      setGeneralMessage("");
      setSuccessMessage(GENERIC_VERIFY_SUCCESS);
      finalizeParentLogin({
        dispatch,
        navigate,
        profileData,
        redirectPath,
      });
    },
    onError: (error) => {
      const responseData = error?.response?.data || {};

      if (isAlreadyVerifiedResponse(responseData)) {
        clearStudentVerificationFlow();
        setGeneralMessage(GENERIC_ALREADY_VERIFIED);
        navigate("/parent/login", { replace: true });
        return;
      }

      if (isExpiredOtpResponse(responseData)) {
        const expiredFlow = markStudentOtpExpired();
        setFlowState(expiredFlow);
        setRemainingMs(0);
        setGeneralMessage(GENERIC_EXPIRED_OTP);
        return;
      }

      const nextFlow = decrementStudentOtpAttempts();
      setFlowState(nextFlow);
      setGeneralMessage(
        nextFlow.attemptsRemaining === 0
          ? GENERIC_ATTEMPTS_EXHAUSTED
          : isInvalidOtpResponse(responseData)
          ? GENERIC_INVALID_OTP
          : GENERIC_FAILURE
      );
    },
  });

  const resendOtpMutation = useMutation({
    mutationFn: ({ email }) => studentAuthApi.resendSignupOtp({ email }),
    onSuccess: (response) => {
      const responsePayload = response?.data || {};

      if (isAlreadyVerifiedResponse(responsePayload)) {
        clearStudentVerificationFlow();
        setGeneralMessage(GENERIC_ALREADY_VERIFIED);
        navigate("/parent/login", { replace: true });
        return;
      }

      const nextFlow = createStudentVerificationFlow({
        ...flowState,
        email: extractStudentEmail(response) || flowState.email,
        preAuthToken: extractStudentPreAuthToken(response),
        verificationType: responsePayload?.data?.verification_type || "otp",
        accountType: responsePayload?.data?.account_type || "student",
        source: "parent_signup",
      });

      setFlowState(nextFlow);
      setRemainingMs(getStudentOtpRemainingMs(nextFlow));
      setOtp("");
      setGeneralMessage("");
      setSuccessMessage(GENERIC_RESEND_SUCCESS);
    },
    onError: (error) => {
      const responseData = error?.response?.data || {};

      if (isAlreadyVerifiedResponse(responseData)) {
        clearStudentVerificationFlow();
        setGeneralMessage(GENERIC_ALREADY_VERIFIED);
        navigate("/parent/login", { replace: true });
        return;
      }

      setGeneralMessage(GENERIC_FAILURE);
    },
  });

  const isExpired = hasStudentOtpExpired(flowState);
  const attemptsRemaining = flowState.attemptsRemaining;
  const canVerify =
    Boolean(flowState.preAuthToken) &&
    attemptsRemaining > 0 &&
    !isExpired &&
    otp.length === 6 &&
    !verifyOtpMutation.isPending;
  const canResend =
    Boolean(flowState.email) &&
    !resendOtpMutation.isPending &&
    flowState.canResendVerification !== false;
  const maskedEmail = maskStudentEmail(flowState.email);
  const helperText =
    attemptsRemaining === 0
      ? GENERIC_ATTEMPTS_EXHAUSTED
      : isExpired
      ? GENERIC_EXPIRED_OTP
      : "Enter the 6-digit code sent to the student email.";

  const handleVerify = (event) => {
    event.preventDefault();

    if (!flowState.email) {
      setGeneralMessage(GENERIC_PENDING_VERIFICATION);
      return;
    }

    if (!flowState.preAuthToken) {
      setGeneralMessage("Request a new verification code to continue.");
      return;
    }

    if (!/^\d{6}$/.test(otp)) {
      setGeneralMessage(GENERIC_INVALID_OTP);
      return;
    }

    if (attemptsRemaining === 0) {
      setGeneralMessage(GENERIC_ATTEMPTS_EXHAUSTED);
      return;
    }

    if (isExpired) {
      markStudentOtpExpired();
      setGeneralMessage(GENERIC_EXPIRED_OTP);
      return;
    }

    setGeneralMessage("");
    setSuccessMessage("");
    verifyOtpMutation.mutate({
      pre_auth_token: flowState.preAuthToken,
      otp,
    });
  };

  const handleResend = () => {
    if (!canResend || !flowState.email) return;

    setGeneralMessage("");
    setSuccessMessage("");
    resendOtpMutation.mutate({
      email: flowState.email,
    });
  };

  return (
    <ParentAuthLayout
      title="Verify the Student Email"
      description="Confirm the student email with the verification code to unlock the full parent flow."
      accent="Secure the account before parents continue"
      accentDescription="Use the OTP sent during signup to verify the student email, then continue into the parent booking experience."
    >
      <div className="flex flex-col items-center text-center">
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[#fff0ec] text-[#ff6b61] shadow-sm">
          <MailCheck size={36} />
        </div>

        <h2 className="mt-5 text-3xl font-bold text-[#111827]">
          Enter the verification code
        </h2>
        <p className="mt-3 text-base leading-7 text-[#475569]">
          {maskedEmail ? `Code sent to ${maskedEmail}` : "Verification code required"}
        </p>
        <p className="mt-1 text-sm leading-6 text-[#64748b]">{helperText}</p>

        <div className="mt-6 grid w-full gap-3 sm:grid-cols-2">
          <div className="rounded-[24px] border border-[#d9e2ff] bg-[#f7f9ff] px-4 py-3 text-center">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#4b5d95]">
              Time Remaining
            </p>
            <p className="mt-2 text-2xl font-bold text-[#08059B]">
              {formatStudentOtpTimer(remainingMs)}
            </p>
          </div>
          <div className="rounded-[24px] border border-[#ffe2d2] bg-[#fff8f3] px-4 py-3 text-center">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#b15b2a]">
              Attempts Remaining
            </p>
            <p className="mt-2 text-2xl font-bold text-[#d46c2d]">
              {attemptsRemaining}
            </p>
          </div>
        </div>

        <form onSubmit={handleVerify} className="mt-6 w-full space-y-4">
          <div>
            <label
              htmlFor="parent-verification-otp"
              className="mb-2 block text-left text-sm font-bold text-[#374151]"
            >
              Verification Code
            </label>
            <div className="relative">
              <input
                id="parent-verification-otp"
                type="text"
                value={otp}
                onChange={(event) => {
                  const nextValue = event.target.value.replace(/\D/g, "").slice(0, 6);
                  setOtp(nextValue);
                  if (generalMessage) setGeneralMessage("");
                }}
                className="w-full rounded-[22px] border border-[#d8cfbf] bg-[#fffaf4] px-4 py-3.5 pr-12 text-center text-2xl tracking-[0.35em] text-[#111827] outline-none transition placeholder:text-[#7c8799] focus:border-[#08059B] focus:bg-white disabled:cursor-not-allowed disabled:opacity-70"
                placeholder="123456"
                inputMode="numeric"
                autoComplete="one-time-code"
                disabled={!flowState.preAuthToken || attemptsRemaining === 0 || isExpired}
              />
              <ShieldCheck
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#64748b]"
                size={22}
              />
            </div>
          </div>

          {generalMessage ? (
            <div className="rounded-[22px] border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
              {generalMessage}
            </div>
          ) : null}

          {successMessage ? (
            <div className="inline-flex w-full items-center justify-center gap-2 rounded-[22px] border border-green-200 bg-green-50 px-4 py-3 text-sm font-medium text-green-700">
              <CheckCircle2 size={18} />
              {successMessage}
            </div>
          ) : null}

          {!flowState.email ? (
            <div className="rounded-[22px] border border-[#e6ded0] bg-[#fcfaf6] px-4 py-3 text-sm leading-7 text-[#475569]">
              There is no active verification session right now. Start from parent signup to request a code.
            </div>
          ) : null}

          <div className="flex flex-col gap-3 sm:flex-row">
            <button
              type="submit"
              disabled={!canVerify}
              className="inline-flex flex-1 items-center justify-center rounded-full bg-[#08059B] px-6 py-3 text-lg font-bold text-white transition hover:bg-[#01044F] disabled:cursor-not-allowed disabled:opacity-70"
            >
              {verifyOtpMutation.isPending ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 size={20} className="animate-spin" />
                  Verifying...
                </span>
              ) : (
                "Verify"
              )}
            </button>

            <button
              type="button"
              onClick={handleResend}
              disabled={!canResend}
              className="inline-flex flex-1 items-center justify-center rounded-full border border-[#08059B] bg-white px-6 py-3 text-lg font-bold text-[#08059B] transition hover:bg-[#f5f7ff] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {resendOtpMutation.isPending ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 size={20} className="animate-spin" />
                  Sending...
                </span>
              ) : (
                "Resend OTP"
              )}
            </button>
          </div>
        </form>

        <div className="mt-6 w-full rounded-[24px] border border-[#e6ded0] bg-[#fcfaf6] px-5 py-4 text-left text-sm leading-7 text-[#475569]">
          <p className="font-bold text-[#111827]">Need a new code?</p>
          <p className="mt-1">
            If the timer expires or all {STUDENT_OTP_ATTEMPTS} attempts are used, request a new verification code.
          </p>
          <button
            type="button"
            onClick={handleResend}
            disabled={!canResend}
            className="mt-3 inline-flex items-center gap-2 font-bold text-[#08059B] underline disabled:no-underline disabled:opacity-60"
          >
            <RotateCcw size={16} />
            Request a new code
          </button>
        </div>

        <div className="mt-6 rounded-[24px] border border-[#e6ded0] bg-white px-5 py-4 text-sm leading-7 text-[#475569]">
          Verified already?{" "}
          <Link className="font-bold text-[#08059B] underline" to="/parent/login">
            Go to Parent Login
          </Link>
        </div>
      </div>
    </ParentAuthLayout>
  );
};

export default ParentVerificationPage;
